import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import Layout from '../components/Layout';

const Leasing = () => {
  const [applications, setApplications] = useState([]);
  const [listings, setListings] = useState([]);
  const [leases, setLeases] = useState([]);
  const [activeTab, setActiveTab] = useState('applications');
  const [showCreateLeaseModal, setShowCreateLeaseModal] = useState(false);
  const [availableUnits, setAvailableUnits] = useState([]);
  const [residents, setResidents] = useState([]);
  const [newLease, setNewLease] = useState({
    unit_id: '',
    resident_id: '',
    lease_start: '',
    lease_end: '',
    rent_amount: '',
    deposit_amount: '',
    status: 'active'
  });
  const [properties, setProperties] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [newApplication, setNewApplication] = useState({
    unit_id: '',
    applicant_name: '',
    applicant_email: '',
    applicant_phone: '',
    move_in_date: '',
    notes: ''
  });

  useEffect(() => {
    fetchLeasingData();
    fetchAvailableUnits();
    fetchResidents();
    fetchProperties();
  }, []);

  const fetchAvailableUnits = async () => {
    const { data } = await supabase
      .from('units')
      .select(`
        *,
        properties(name),
        buildings(building_name)
      `)
      .eq('status', 'vacant')
      .order('unit_number');
    setAvailableUnits(data || []);
  };

  const fetchProperties = async () => {
    const { data } = await supabase
      .from('properties')
      .select('*')
      .order('name');
    setProperties(data || []);
  };

  const fetchBuildings = async (propertyId) => {
    if (!propertyId) {
      setBuildings([]);
      return;
    }
    const { data } = await supabase
      .from('buildings')
      .select('*')
      .eq('property_id', propertyId)
      .order('building_name');
    setBuildings(data || []);
  };

  const fetchResidents = async () => {
    const { data } = await supabase
      .from('residents')
      .select('*')
      .eq('account_type', 'resident')
      .eq('status', 'active')
      .order('first_name');
    setResidents(data || []);
  };

  const fetchLeasingData = async () => {
    // Fetch applications
    const { data: appData } = await supabase
      .from('lease_applications')
      .select(`
        *,
        units(unit_number, rent_amount, properties(name))
      `)
      .order('created_at', { ascending: false });
    setApplications(appData || []);

    // Fetch listings
    const { data: listingData } = await supabase
      .from('listings')
      .select(`
        *,
        units(unit_number, bedrooms, bathrooms, properties(name))
      `)
      .order('created_at', { ascending: false });
    setListings(listingData || []);

    // Fetch active leases
    const { data: leaseData } = await supabase
      .from('leases')
      .select(`
        *,
        units(unit_number, properties(name)),
        residents(first_name, last_name, email, phone)
      `)
      .eq('status', 'active')
      .order('lease_end', { ascending: true });
    setLeases(leaseData || []);
  };

  const updateApplicationStatus = async (applicationId, status) => {
    await supabase
      .from('lease_applications')
      .update({ status })
      .eq('id', applicationId);
    fetchLeasingData();
  };

  const createLease = async (application) => {
    try {
      // Create resident record with login disabled initially
      const { data: resident } = await supabase
        .from('residents')
        .insert({
          first_name: application.applicant_name.split(' ')[0],
          last_name: application.applicant_name.split(' ').slice(1).join(' '),
          email: application.applicant_email,
          phone: application.applicant_phone,
          account_type: 'resident',
          status: 'active',
          login_enabled: false // Enable after lease signing
        })
        .select()
        .single();

      // Create lease
      await supabase
        .from('leases')
        .insert({
          unit_id: application.unit_id,
          resident_id: resident.id,
          lease_start: new Date().toISOString().split('T')[0],
          lease_end: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          rent_amount: application.units.rent_amount,
          deposit_amount: application.units.rent_amount,
          status: 'active'
        });

      // Update unit status
      await supabase
        .from('units')
        .update({ status: 'occupied' })
        .eq('id', application.unit_id);

      // Update application status
      await updateApplicationStatus(application.id, 'approved');
      showToast('Lease created successfully! Resident account created.', 'success');
    } catch (error) {
      console.error('Error creating lease:', error);
      showToast('Error creating lease. Please try again.', 'error');
    }
  };

  const renewLease = async (lease) => {
    try {
      const newEndDate = new Date(lease.lease_end);
      newEndDate.setFullYear(newEndDate.getFullYear() + 1);
      
      await supabase
        .from('leases')
        .update({
          lease_end: newEndDate.toISOString().split('T')[0]
        })
        .eq('id', lease.id);
      
      fetchLeasingData();
      alert('Lease renewed successfully!');
    } catch (error) {
      console.error('Error renewing lease:', error);
      alert('Error renewing lease. Please try again.');
    }
  };

  const viewLeaseDetails = (lease) => {
    alert(`Lease Details:\nTenant: ${lease.residents?.first_name} ${lease.residents?.last_name}\nUnit: ${lease.units?.properties?.name} - ${lease.units?.unit_number}\nRent: $${lease.rent_amount}\nLease End: ${new Date(lease.lease_end).toLocaleDateString()}`);
  };

  const handleCreateLease = async (e) => {
    e.preventDefault();
    try {
      // Create the lease
      await supabase
        .from('leases')
        .insert(newLease);

      // Update unit status to occupied
      await supabase
        .from('units')
        .update({ status: 'occupied' })
        .eq('id', newLease.unit_id);

      setShowCreateLeaseModal(false);
      setNewLease({
        unit_id: '',
        resident_id: '',
        lease_start: '',
        lease_end: '',
        rent_amount: '',
        deposit_amount: '',
        status: 'active'
      });
      fetchLeasingData();
      fetchAvailableUnits();
      showToast('Lease created successfully!', 'success');
    } catch (error) {
      console.error('Error creating lease:', error);
      showToast('Error creating lease. Please try again.', 'error');
    }
  };

  const showToast = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  };

  const handleCreateApplication = async (e) => {
    e.preventDefault();
    try {
      await supabase
        .from('lease_applications')
        .insert({
          ...newApplication,
          tenant_id: '1', // Get from context
          status: 'pending'
        });
      
      setShowApplicationModal(false);
      setNewApplication({
        unit_id: '',
        applicant_name: '',
        applicant_email: '',
        applicant_phone: '',
        move_in_date: '',
        notes: ''
      });
      fetchLeasingData();
      showToast('Application created successfully!', 'success');
    } catch (error) {
      showToast('Error creating application.', 'error');
    }
  };

  const handleUnitChange = (unitId) => {
    const selectedUnit = availableUnits.find(u => u.id === unitId);
    setNewLease({
      ...newLease,
      unit_id: unitId,
      rent_amount: selectedUnit?.rent_amount || '',
      deposit_amount: selectedUnit?.deposit_amount || ''
    });
  };

  const handlePropertyChange = (propertyId) => {
    setSelectedProperty(propertyId);
    setSelectedBuilding('');
    fetchBuildings(propertyId);
  };

  const getFilteredUnits = () => {
    let filtered = availableUnits;
    
    if (selectedProperty) {
      filtered = filtered.filter(unit => unit.property_id === selectedProperty);
    }
    
    if (selectedBuilding) {
      filtered = filtered.filter(unit => unit.building_id === selectedBuilding);
    }
    
    return filtered;
  };

  return (
    <Layout>
      <div style={{ padding: '20px' }}>
        <h2>Leasing & Marketing</h2>
        
        <div style={{ display: 'flex', gap: '20px', marginBottom: '30px' }}>
          <button 
            onClick={() => setActiveTab('applications')}
            className={`btn ${activeTab === 'applications' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Applications ({applications.filter(a => a.status === 'pending').length})
          </button>
          <button 
            onClick={() => setActiveTab('listings')}
            className={`btn ${activeTab === 'listings' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Active Listings ({listings.filter(l => l.status === 'active').length})
          </button>
          <button 
            onClick={() => setActiveTab('leases')}
            className={`btn ${activeTab === 'leases' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Active Leases ({leases.length})
          </button>
          <button 
            onClick={() => setActiveTab('screening')}
            className={`btn ${activeTab === 'screening' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Screening
          </button>
        </div>

        {activeTab === 'applications' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>Lease Applications</h3>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn btn-secondary">Export Applications</button>
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowApplicationModal(true)}
                >
                  Add Application
                </button>
              </div>
            </div>
            
            <div style={{ display: 'grid', gap: '15px' }}>
              {applications.map(app => (
                <div key={app.id} style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ flex: 1 }}>
                      <h4>{app.applicant_name}</h4>
                      <p style={{ color: '#666', margin: '5px 0' }}>
                        {app.units?.properties?.name} - Unit {app.units?.unit_number}
                      </p>
                      <p style={{ color: '#666', margin: '5px 0' }}>
                        {app.applicant_email} | {app.applicant_phone}
                      </p>
                      <p style={{ fontSize: '12px', color: '#999' }}>
                        Applied: {new Date(app.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'end' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        background: app.status === 'approved' ? '#d4edda' : app.status === 'denied' ? '#f8d7da' : '#fff3cd',
                        color: app.status === 'approved' ? '#155724' : app.status === 'denied' ? '#721c24' : '#856404'
                      }}>
                        {app.status}
                      </span>
                      
                      {app.status === 'pending' && (
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button 
                            onClick={() => alert(`Application Details:\nName: ${app.applicant_name}\nEmail: ${app.applicant_email}\nPhone: ${app.applicant_phone}\nUnit: ${app.units?.properties?.name} - ${app.units?.unit_number}`)}
                            className="btn btn-secondary"
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                          >
                            Review
                          </button>
                          <button 
                            onClick={() => createLease(app)}
                            className="btn btn-primary"
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                          >
                            Approve
                          </button>
                          <button 
                            onClick={() => updateApplicationStatus(app.id, 'denied')}
                            className="btn btn-secondary"
                            style={{ fontSize: '12px', padding: '4px 8px', background: '#dc3545', color: 'white' }}
                          >
                            Deny
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'listings' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>Marketing Listings</h3>
              <button className="btn btn-primary">Create Listing</button>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
              {listings.map(listing => (
                <div key={listing.id} style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                  <div style={{ height: '200px', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ color: '#666' }}>📷 Photos</span>
                  </div>
                  <div style={{ padding: '15px' }}>
                    <h4>{listing.title}</h4>
                    <p style={{ color: '#666', fontSize: '14px' }}>
                      {listing.units?.properties?.name} - Unit {listing.units?.unit_number}
                    </p>
                    <p style={{ color: '#666', fontSize: '14px' }}>
                      {listing.units?.bedrooms}BR/{listing.units?.bathrooms}BA
                    </p>
                    <p style={{ fontSize: '18px', fontWeight: 'bold', color: '#007bff', margin: '10px 0' }}>
                      ${listing.rent_amount}/month
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        background: listing.status === 'active' ? '#d4edda' : '#f8d7da',
                        color: listing.status === 'active' ? '#155724' : '#721c24'
                      }}>
                        {listing.status}
                      </span>
                      <button className="btn btn-secondary" style={{ fontSize: '12px' }}>Edit</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'leases' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>Active Leases</h3>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn btn-secondary">Renewal Notices</button>
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowCreateLeaseModal(true)}
                >
                  Create Lease
                </button>
              </div>
            </div>
            
            <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: '#f8f9fa' }}>
                  <tr>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Tenant</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Property/Unit</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Rent</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Lease End</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leases.map(lease => (
                    <tr key={lease.id}>
                      <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                        <div>
                          <strong>{lease.residents?.first_name} {lease.residents?.last_name}</strong>
                          <p style={{ fontSize: '12px', color: '#666', margin: '2px 0' }}>
                            {lease.residents?.email}
                          </p>
                        </div>
                      </td>
                      <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                        {lease.units?.properties?.name} - {lease.units?.unit_number}
                      </td>
                      <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                        ${lease.rent_amount}
                      </td>
                      <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                        {new Date(lease.lease_end).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          background: new Date(lease.lease_end) < new Date(Date.now() + 90 * 24 * 60 * 60 * 1000) ? '#fff3cd' : '#d4edda',
                          color: new Date(lease.lease_end) < new Date(Date.now() + 90 * 24 * 60 * 60 * 1000) ? '#856404' : '#155724'
                        }}>
                          {new Date(lease.lease_end) < new Date(Date.now() + 90 * 24 * 60 * 60 * 1000) ? 'Renewal Due' : 'Active'}
                        </span>
                      </td>
                      <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button 
                            onClick={() => viewLeaseDetails(lease)}
                            className="btn btn-secondary" 
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                          >
                            View
                          </button>
                          <button 
                            onClick={() => renewLease(lease)}
                            className="btn btn-primary" 
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                          >
                            Renew
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'screening' && (
          <div>
            <h3>Tenant Screening</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
              <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <h4>Credit Checks</h4>
                <p>Automated credit screening</p>
                <button className="btn btn-primary">Run Credit Check</button>
              </div>
              <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <h4>Background Checks</h4>
                <p>Criminal background verification</p>
                <button className="btn btn-primary">Background Check</button>
              </div>
              <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <h4>Income Verification</h4>
                <p>Employment and income validation</p>
                <button className="btn btn-primary">Verify Income</button>
              </div>
              <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <h4>Reference Checks</h4>
                <p>Previous landlord references</p>
                <button className="btn btn-primary">Check References</button>
              </div>
            </div>
          </div>
        )}

        {/* Create Lease Modal */}
        {showCreateLeaseModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', padding: '30px', borderRadius: '8px', width: '500px', maxHeight: '90vh', overflow: 'auto' }}>
              <h3>Create New Lease</h3>
              <form onSubmit={handleCreateLease}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                  <div className="form-group">
                    <label>Filter by Property</label>
                    <select
                      value={selectedProperty}
                      onChange={(e) => handlePropertyChange(e.target.value)}
                    >
                      <option value="">All Properties</option>
                      {properties.map(property => (
                        <option key={property.id} value={property.id}>
                          {property.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Filter by Building</label>
                    <select
                      value={selectedBuilding}
                      onChange={(e) => setSelectedBuilding(e.target.value)}
                      disabled={!selectedProperty}
                    >
                      <option value="">All Buildings</option>
                      {buildings.map(building => (
                        <option key={building.id} value={building.id}>
                          {building.building_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Unit *</label>
                  <select
                    value={newLease.unit_id}
                    onChange={(e) => handleUnitChange(e.target.value)}
                    required
                  >
                    <option value="">Select Unit</option>
                    {getFilteredUnits().map(unit => (
                      <option key={unit.id} value={unit.id}>
                        {unit.properties?.name} - {unit.buildings?.building_name ? `${unit.buildings.building_name} - ` : ''}Unit {unit.unit_number} (${unit.rent_amount}/mo)
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Resident *</label>
                  <select
                    value={newLease.resident_id}
                    onChange={(e) => setNewLease({...newLease, resident_id: e.target.value})}
                    required
                  >
                    <option value="">Select Resident</option>
                    {residents.map(resident => (
                      <option key={resident.id} value={resident.id}>
                        {resident.first_name} {resident.last_name} - {resident.email}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label>Lease Start *</label>
                    <input
                      type="date"
                      value={newLease.lease_start}
                      onChange={(e) => setNewLease({...newLease, lease_start: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Lease End *</label>
                    <input
                      type="date"
                      value={newLease.lease_end}
                      onChange={(e) => setNewLease({...newLease, lease_end: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label>Monthly Rent *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newLease.rent_amount}
                      onChange={(e) => setNewLease({...newLease, rent_amount: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Security Deposit *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newLease.deposit_amount}
                      onChange={(e) => setNewLease({...newLease, deposit_amount: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button type="submit" className="btn btn-primary">Create Lease</button>
                  <button 
                    type="button" 
                    onClick={() => setShowCreateLeaseModal(false)} 
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Application Modal */}
        {showApplicationModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', padding: '30px', borderRadius: '8px', width: '500px' }}>
              <h3>Add New Application</h3>
              <form onSubmit={handleCreateApplication}>
                <div className="form-group">
                  <label>Unit *</label>
                  <select
                    value={newApplication.unit_id}
                    onChange={(e) => setNewApplication({...newApplication, unit_id: e.target.value})}
                    required
                  >
                    <option value="">Select Unit</option>
                    {getFilteredUnits().map(unit => (
                      <option key={unit.id} value={unit.id}>
                        {unit.properties?.name} - {unit.buildings?.building_name ? `${unit.buildings.building_name} - ` : ''}Unit {unit.unit_number} (${unit.rent_amount}/mo)
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Applicant Name *</label>
                  <input
                    type="text"
                    value={newApplication.applicant_name}
                    onChange={(e) => setNewApplication({...newApplication, applicant_name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={newApplication.applicant_email}
                    onChange={(e) => setNewApplication({...newApplication, applicant_email: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone *</label>
                  <input
                    type="tel"
                    value={newApplication.applicant_phone}
                    onChange={(e) => setNewApplication({...newApplication, applicant_phone: e.target.value})}
                    required
                  />
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button type="submit" className="btn btn-primary">Create Application</button>
                  <button 
                    type="button" 
                    onClick={() => setShowApplicationModal(false)} 
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Toast Notification */}
        {toast.show && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            background: toast.type === 'success' ? '#d4edda' : '#f8d7da',
            color: toast.type === 'success' ? '#155724' : '#721c24',
            padding: '12px 20px',
            borderRadius: '8px',
            border: `1px solid ${toast.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1001,
            minWidth: '300px',
            animation: 'slideIn 0.3s ease'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{toast.type === 'success' ? '✅' : '❌'}</span>
              <span>{toast.message}</span>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Leasing;